import { ReactNode } from 'react';
import { Button, makeStyles, Typography } from '@material-ui/core';
import { useEntityNames } from '../useEntityNames';
import { shortRef, TYPE_CHIPS, TypeChip } from './listFilters';

/**
 * What the list says when it has nothing to show — and why, because each empty
 * list here has a different cause and a different way out.
 *
 * "Mine" is the one that matters most. Until every colleague's sign-in carries
 * their team (DX-202), it is empty for most people, and an empty tab that just
 * says "nothing here" reads as a broken portal. So it says which of two things
 * is true: the sign-in carries no team, or the team owns nothing — and those
 * are fixed by different people.
 *
 * MUI v4 only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  box: {
    padding: theme.spacing(5, 3),
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  title: { fontWeight: 600, color: 'var(--ui-text)' },
  body: {
    maxWidth: '34rem',
    color: 'var(--ui-text-muted)',
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  action: { marginTop: theme.spacing(1) },
}));

const Empty = ({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: { label: string; onClick: () => void };
}) => {
  const classes = useStyles();
  return (
    <div className={classes.box} role="status">
      <Typography component="h2" className={classes.title}>
        {title}
      </Typography>
      {children && (
        <Typography component="p" className={classes.body}>
          {children}
        </Typography>
      )}
      {action && (
        <Button
          variant="outlined"
          size="small"
          className={classes.action}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
};

/** Mine, with nothing in it. */
export const EmptyMine = ({
  ownership,
  onShowAll,
}: {
  ownership: string[];
  onShowAll: () => void;
}) => {
  // By the names the portal shows for them, and by their short reference
  // until the presentation API has answered or where it is not there.
  const teamRefs = ownership.filter(ref => ref.startsWith('group:'));
  const teams = useEntityNames(teamRefs).map(
    (name, i) => name ?? shortRef(teamRefs[i], 'group'),
  );
  const showAll = { label: 'Show everything', onClick: onShowAll };

  if (!teams.length) {
    return (
      <Empty title="Your sign-in is not linked to a team" action={showAll}>
        Mine lists what your team owns, and your revenexx ID does not name a
        team yet — so nothing in the catalog can be counted as yours. An admin
        adds your team to your profile; until then, All lists everything.
      </Empty>
    );
  }
  const named =
    teams.length === 1
      ? teams[0]
      : `${teams.slice(0, -1).join(', ')} or ${teams[teams.length - 1]}`;
  return (
    <Empty title={`Nothing is owned by ${named} yet`} action={showAll}>
      Mine lists what your team owns, and no component or API in the catalog
      names {named} as its owner. An entry names its owner in its own catalog
      file.
    </Empty>
  );
};

/** Starred, with nothing in it. */
export const EmptyStarred = ({ onShowAll }: { onShowAll: () => void }) => (
  <Empty
    title="Nothing starred yet"
    action={{ label: 'Show everything', onClick: onShowAll }}
  >
    Star a component or an API — here in the list, or on its own page — and it
    is listed here and in the sidebar.
  </Empty>
);

/** The catalog itself holds no component and no API. */
export const EmptyCatalog = () => (
  <Empty title="The catalog holds no components or APIs yet">
    A repository appears here once it carries a catalog file. Register one that
    already has, or create a component from a template.
  </Empty>
);

/** The tab has entries, but none of this type or under these filters. */
export const EmptyScope = ({
  chip,
  filtered,
  onClear,
}: {
  chip: TypeChip;
  filtered: boolean;
  onClear: () => void;
}) => {
  const label = TYPE_CHIPS.find(c => c.id === chip)!.label;
  const what =
    chip === 'all' ? 'Nothing' : `No ${label.toLocaleLowerCase('en-US')}`;
  return (
    <Empty
      title={`${what} ${filtered ? 'under these filters' : 'here'}`}
      action={{
        label: filtered ? 'Clear the filters' : 'Show all types',
        onClick: onClear,
      }}
    />
  );
};

/** Nothing matches the typed text. */
export const EmptyMatch = ({
  text,
  onClear,
}: {
  text: string;
  onClear: () => void;
}) => (
  <Empty
    title={`Nothing matches “${text.trim()}”`}
    action={{ label: 'Clear the filter', onClick: onClear }}
  >
    The filter looks at names, titles, tags, owners and systems.
  </Empty>
);
